/**
 * Button — Meridian's primary interactive control.
 *
 * Enforces the 44px touch floor and visible focus by default. Variants keep
 * brand violet for primary actions and the critical ramp for destructive ones
 * (the only status-color use permitted outside StatusPill, because a destructive
 * button IS communicating operational risk).
 */
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

const VARIANTS: Record<Variant, string> = {
  primary: "bg-brand text-ink-invert hover:bg-brand-strong",
  secondary: "border border-line bg-surface text-ink hover:bg-surface-2",
  ghost: "text-ink hover:bg-surface-2",
  danger: "bg-critical text-ink-invert hover:opacity-90",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        // min-h-touch (44px) satisfies the tablet touch floor.
        "inline-flex min-h-touch items-center justify-center gap-2 rounded px-4 text-sm font-medium",
        "transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
});
